// Agent/Skill package browser (roadmap item 149 Phase B).
//
// Mounted at `/repos/:id/agentskill` for any repository whose `format` is
// `agentskill`. Phase A (`api/internal/formats/agentskill`) already parses
// every published tarball's manifest into the artifact's JSONB `metadata`
// column, so this page needs no new backend endpoint — it reuses the
// generic `useRepoArtifacts` hook (same one RepoDetail's Contents tab
// calls) and groups rows client-side by `metadata.name`, mirroring the
// Ollama/Kustomize/OPA browsers' catalog-then-detail shape.
//
// Deliberately out of scope for this batch (left for a follow-up, same
// pattern as item 140's staged batches): a live "signed / scanned /
// released" policy-status readout. No per-artifact endpoint exposing that
// as a single field exists yet in the frontend API surface; each version
// row instead links to the existing generic `/artifacts/:id` page, which
// already renders provenance/build-info when available.

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  Download,
  ExternalLink,
  FileText,
  MessageSquare,
  Package,
  Server,
  ShieldQuestion,
  Wrench,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { useProjects, useRepoDetail, useRepoArtifacts, type RepoArtifact } from '../../api/client';

const PACKAGE_KIND_DISCRIMINATOR = 'agentskill.package';

function bytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function shortHex(d?: string): string {
  if (!d) return '';
  if (d.length <= 19) return d;
  return d.slice(0, 12) + '…' + d.slice(-4);
}

const KIND_META: Record<string, { label: string; icon: typeof Bot }> = {
  skill: { label: 'Skill', icon: Wrench },
  agent: { label: 'Agent', icon: Bot },
  'mcp-server': { label: 'MCP server', icon: Server },
  prompt: { label: 'Prompt', icon: MessageSquare },
};

function kindMeta(packageKind: unknown): { label: string; icon: typeof Bot } {
  const k = typeof packageKind === 'string' ? packageKind : '';
  return KIND_META[k] ?? { label: k || 'Unknown', icon: ShieldQuestion };
}

interface SkillGroup {
  name: string;
  packageKind: unknown;
  description: string;
  versions: RepoArtifact[];
}

function groupPackages(artifacts: RepoArtifact[] | undefined): SkillGroup[] {
  if (!artifacts) return [];
  const byName = new Map<string, RepoArtifact[]>();
  for (const a of artifacts) {
    if (a.metadata?.kind !== PACKAGE_KIND_DISCRIMINATOR) continue;
    const name = typeof a.metadata.name === 'string' ? a.metadata.name : a.path;
    const list = byName.get(name) ?? [];
    list.push(a);
    byName.set(name, list);
  }
  const groups: SkillGroup[] = [];
  for (const [name, versions] of byName) {
    versions.sort((x, y) => (x.uploaded_at < y.uploaded_at ? 1 : -1));
    const latest = versions[0];
    groups.push({
      name,
      packageKind: latest.metadata?.package_kind,
      description:
        typeof latest.metadata?.description === 'string' ? latest.metadata.description : '',
      versions,
    });
  }
  groups.sort((a, b) => (a.versions[0].uploaded_at < b.versions[0].uploaded_at ? 1 : -1));
  return groups;
}

export function AgentSkillPackages() {
  const { id } = useParams<{ id: string }>();
  const { data: repo, isLoading: repoLoading, error: repoError } = useRepoDetail(id);
  const { data: projects } = useProjects();
  const projectSlug = useMemo(
    () => projects?.find((p) => p.id === repo?.projectId)?.slug,
    [projects, repo?.projectId],
  );
  const { data: artifacts, error: artifactsError, isLoading: artifactsLoading } =
    useRepoArtifacts(repo?.id, 1000);
  const groups = useMemo(() => groupPackages(artifacts), [artifacts]);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const selected = groups.find((g) => g.name === selectedName) ?? null;

  if (repoLoading) {
    return <div className="p-6 text-fg-muted">Loading repository…</div>;
  }
  if (repoError || !repo) {
    return (
      <div className="p-6 text-danger">
        Failed to load repository: {String(repoError ?? 'unknown')}
      </div>
    );
  }
  if (repo.format !== 'agentskill') {
    return (
      <div className="p-6">
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to repository
        </Link>
        <p className="text-fg-muted">
          This page is only available for repositories of format{' '}
          <code className="orb-code">agentskill</code>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {repo.name}
        </Link>
        <p className="orb-eyebrow text-accent/80 mb-1">Agent / Skill packages</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">
          {repo.name}
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-3xl">
          Skill, agent, MCP-server, and prompt packages published to this
          repository. Each row is one package name; click it to see the
          declared tools/permissions/egress, the <code className="orb-code">SKILL.md</code>{' '}
          description, and every published version.
        </p>
      </div>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Package className="h-4 w-4 text-accent" />
              Catalog
            </span>
          }
          description={
            groups.length > 0
              ? `${groups.length} package${groups.length === 1 ? '' : 's'}`
              : undefined
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Kind</th>
                <th className="px-4 py-2 font-medium">Description</th>
                <th className="px-4 py-2 font-medium">Versions</th>
                <th className="px-4 py-2 font-medium">Latest</th>
                <th className="px-4 py-2 font-medium">Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {artifactsLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    Loading…
                  </td>
                </tr>
              )}
              {artifactsError && (
                <tr>
                  <td className="px-4 py-3 text-danger" colSpan={6}>
                    {String(artifactsError)}
                  </td>
                </tr>
              )}
              {groups.length === 0 && !artifactsLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    No packages published yet. Publish a{' '}
                    <code className="orb-code">.tgz</code> with a root{' '}
                    <code className="orb-code">manifest.json</code> to{' '}
                    <code className="orb-code">
                      /agentskill/{projectSlug}/{repo.slug}/
                    </code>
                    .
                  </td>
                </tr>
              )}
              {groups.map((g) => {
                const { label, icon: Icon } = kindMeta(g.packageKind);
                const latest = g.versions[0];
                const isSelected = g.name === selectedName;
                return (
                  <tr
                    key={g.name}
                    className={`border-b border-border/50 cursor-pointer ${
                      isSelected ? 'bg-bg-hover/60' : 'hover:bg-bg-hover/40'
                    }`}
                    onClick={() => setSelectedName(g.name)}
                  >
                    <td className="px-4 py-2 font-medium text-fg">
                      <span className="inline-flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5 text-accent" />
                        {g.name}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone="accent">{label}</Badge>
                    </td>
                    <td className="px-4 py-2 text-fg-muted max-w-xs truncate" title={g.description}>
                      {g.description || '—'}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">{g.versions.length}</td>
                    <td className="px-4 py-2">
                      <Badge tone="neutral">{latest.version}</Badge>
                    </td>
                    <td className="px-4 py-2 text-fg-muted">
                      {new Date(latest.uploaded_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {selected && (
        <SkillDetailCard group={selected} projectSlug={projectSlug} repoSlug={repo.slug} />
      )}
    </div>
  );
}

interface SkillDetailCardProps {
  readonly group: SkillGroup;
  readonly projectSlug: string | undefined;
  readonly repoSlug: string;
}

function SkillDetailCard({ group, projectSlug, repoSlug }: SkillDetailCardProps) {
  const latest = group.versions[0];
  const meta = latest.metadata ?? {};
  const { label, icon: Icon } = kindMeta(group.packageKind);
  const readme = typeof meta.readme === 'string' ? meta.readme : '';

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Icon className="h-4 w-4 text-accent" />
            {group.name}
            <Badge tone="accent">{label}</Badge>
            <Badge tone="neutral">{latest.version}</Badge>
          </span>
        }
        description={group.description || 'No description declared in the manifest.'}
      />
      <CardBody>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <ManifestField label="Tools" value={meta.tools} />
            <ManifestField label="Permissions" value={meta.permissions} />
            <ManifestField label="Egress" value={meta.egress} />
          </div>

          {typeof meta.dependencies !== 'undefined' && (
            <ManifestField label="Dependencies" value={meta.dependencies} />
          )}

          <div>
            <h3 className="text-sm font-semibold text-fg mb-2 inline-flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-accent" />
              SKILL.md
            </h3>
            {readme ? (
              <pre className="bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre-wrap max-h-[420px]">
                {readme}
              </pre>
            ) : (
              <p className="text-fg-muted text-sm">
                This package has no <code className="orb-code">SKILL.md</code> at the archive root.
              </p>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-fg mb-2">
              Versions ({group.versions.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-fg-muted border-b border-border">
                    <th className="px-3 py-2 font-medium">Version</th>
                    <th className="px-3 py-2 font-medium">Size</th>
                    <th className="px-3 py-2 font-medium">sha256</th>
                    <th className="px-3 py-2 font-medium">Uploaded</th>
                    <th className="px-3 py-2 font-medium">Links</th>
                  </tr>
                </thead>
                <tbody>
                  {group.versions.map((v) => (
                    <tr key={v.id} className="border-b border-border/50">
                      <td className="px-3 py-2">
                        <Badge tone="neutral">{v.version}</Badge>
                      </td>
                      <td className="px-3 py-2 tabular-nums text-fg-muted">
                        {bytes(v.size_bytes)}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-fg-muted">
                        {shortHex(v.sha256)}
                      </td>
                      <td className="px-3 py-2 text-fg-muted">
                        {new Date(v.uploaded_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-3">
                          <Link
                            to={`/artifacts/${v.id}`}
                            className="inline-flex items-center gap-1 text-accent hover:underline"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            Detail
                          </Link>
                          <a
                            href={`/agentskill/${projectSlug}/${repoSlug}/${encodeURIComponent(
                              group.name,
                            )}/${encodeURIComponent(v.version)}/download`}
                            className="inline-flex items-center gap-1 text-fg-muted hover:text-fg"
                          >
                            <Download className="h-3.5 w-3.5" />
                            Download
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

interface ManifestFieldProps {
  readonly label: string;
  readonly value: unknown;
}

// Tools/permissions/egress/dependencies round-trip through the manifest as
// arbitrary JSON (see agentskill.go's parsePackage) — a plain string array,
// an array of objects, or something else entirely. Render the common shapes
// legibly and fall back to pretty-printed JSON for everything else.
function ManifestField({ label, value }: ManifestFieldProps) {
  return (
    <div>
      <h4 className="text-fg-muted text-xs uppercase tracking-wide mb-2">{label}</h4>
      {renderManifestValue(value)}
    </div>
  );
}

function renderManifestValue(value: unknown) {
  if (value === undefined || value === null) {
    return <p className="text-fg-muted text-sm">Not declared.</p>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <p className="text-fg-muted text-sm">None declared.</p>;
    }
    if (value.every((v) => typeof v === 'string' || typeof v === 'number')) {
      return (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((v, i) => (
            <li key={i}>
              <Badge tone="neutral">{String(v)}</Badge>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <pre className="bg-bg-inset border border-border rounded-md p-2 text-xs font-mono text-fg overflow-x-auto whitespace-pre max-h-48">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  if (typeof value === 'object') {
    return (
      <pre className="bg-bg-inset border border-border rounded-md p-2 text-xs font-mono text-fg overflow-x-auto whitespace-pre max-h-48">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return <p className="text-fg text-sm">{String(value)}</p>;
}
